import {
  FlexBoxImageScaleView,
  FlexBoxView,
} from '@play-co/timestep-core/flexbox';
import { ViewOpts } from '@play-co/timestep-core/lib/ui/View';
import gsap from 'gsap';
import ButtonScaleView from 'src/lib/ui/components/ButtonScaleView';

const assets = {
  active: 'assets/ui/shared/buttons/checkmark_container_active.png',
  inactive: 'assets/ui/shared/buttons/checkmark_container.png',
  checkmark: 'assets/ui/shared/icons/checkmark.png',
};

const checkmarkAssetDimensions = {
  width: 84,
  height: 68,
};

const baseSize = 36;
const tweenDuration = 0.15;

type CheckboxOpts = Omit<ViewOpts, 'width' | 'height'> & {
  tracking: {
    feature: string;
    $subFeature: string;
  };
  onChange?: (value: boolean) => void;
  size?: number;
  isChecked?: boolean;
};

export default class Checkbox extends ButtonScaleView {
  protected readonly overlay: FlexBoxImageScaleView;
  protected readonly checkMark: FlexBoxImageScaleView;
  public onChange: ((value: boolean) => void) | undefined;
  protected _isChecked = false;

  public get isChecked(): boolean {
    return this._isChecked;
  }

  private set isChecked(value: boolean) {
    this._isChecked = value;
    const key = value ? 'active' : 'inactive';
    this.setImage(assets[key]);
    this.overlay.image.setImage(assets[value ? 'inactive' : 'active']);
  }

  constructor(opts: CheckboxOpts) {
    const { size = baseSize, onChange, isChecked = false } = opts;

    super({
      width: size,
      height: size,
      ...opts,
      onClick: async () => this.toggle(),
    });

    const wrapper = new FlexBoxView({
      superview: this,
      flex: {
        width: size,
        height: size,
      },
    });

    this.overlay = new FlexBoxImageScaleView({
      superview: wrapper,
      flex: {
        position: 'absolute',
        width: '100%',
        height: '100%',
      },
    });

    this.checkMark = new FlexBoxImageScaleView({
      superview: wrapper,
      canHandleEvents: false,
      image: assets.checkmark,
      verticalAlign: 'center',
      horizontalAlign: 'center',
      centerAnchor: true,
      flex: {
        position: 'absolute',
        aspectRatio:
          checkmarkAssetDimensions.width / checkmarkAssetDimensions.height,
        width: '100%',
        left: '20%',
        bottom: '25%',
      },
    });

    this.onChange = onChange;
    this.updateState(isChecked, true, true);
  }

  /**
   * Toggle between the checked state of the checkbox.
   */
  public toggle() {
    const value = !this.isChecked;
    this.setChecked(value);
  }

  /**
   * Set the checked state of the checkbox and handle
   * the callback and visual state update accordingly.
   */
  public setChecked(value: boolean, instant = false) {
    if (this.isChecked === value) return;
    this.updateState(value, instant);
    this.onChange?.(value);
  }

  /**
   * Update visual state of the checkbox.
   */
  public updateState(value: boolean, instant = false, force = false) {
    if (!force && this.isChecked === value) return;
    this.isChecked = value;
    this.animatePanel(instant);
    this.animateCheckmark(value, instant);
  }

  private async animatePanel(instant = false) {
    this.overlay.updateOpts({ visible: true, opacity: 1 });
    await gsap.to(this.overlay.style, {
      duration: instant ? 0 : tweenDuration,
      opacity: 0,
      ease: 'quad.inOut',
      overwrite: true,
    });
    this.overlay.updateOpts({ visible: false });
  }

  private async animateCheckmark(value: boolean, instant = false) {
    return gsap.to(this.checkMark.style, {
      duration: instant ? 0 : tweenDuration,
      scale: value ? 1 : 0,
      ease: 'back.out',
      overwrite: true,
    });
  }
}
